import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { usePaintBanner } from './usePaintBanner'
import PrepackStyleAction from './PrepackStyleAction'
import { bannerFramingWash, bannerImageOf } from './prepackCardModel'
import BannerFraming from './BannerFraming'
import { NativeAssetControl } from '../native-sportsbook/NativeAssetControl'
import { usePictureMediaAccess } from './usePictureMediaAccess'
import { usePrepackPreview } from './prepackPreviewContext'
import { brandingToCssVars, resolveBrandingWithOverrides } from '@feed-widget/branding'
import { getFallbackTemplate, getTemplateVariant } from '../../../../../shared/pkg/prepacks/templateVariants.js'
import { NATIVE_COLOR_VARIABLES } from '../../../../../shared/pkg/prepacks/templateRendering.js'
import { BANNER_FILL_CARD, BANNER_FILL_VARIABLE, BANNER_HIDDEN_VAR, BANNER_IMAGE_VARIABLE, BANNER_MODE_DYNAMIC, BANNER_MODE_STATIC, BANNER_MODE_VAR, BANNER_POSITION_VARIABLE, BANNER_PROMPT_VAR, BANNER_SIZE_VARIABLE, MAX_BANNER_PROMPT_RUNES, bannerImageSize, bannerImageUrl, bannerImageValue, bannerScaleFromSize, bannerTitleOf, isBannerImageUrl, packBannerFill, templateWithBannerFill } from '../../../../../shared/pkg/prepacks/templateBuilder.js'

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i
const SHORT_HEX_COLOR_PATTERN = /^#[0-9a-f]{3}$/i
const HEX_COLOR_LITERAL_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

function colorInputValue(value) {
  const text = String(value || '').trim()
  if (HEX_COLOR_PATTERN.test(text)) return text
  if (SHORT_HEX_COLOR_PATTERN.test(text)) {
    return `#${text.slice(1).split('').map((character) => character + character).join('')}`
  }
  return '#000000'
}

function isColorInputValue(value) {
  return HEX_COLOR_LITERAL_PATTERN.test(String(value || '').trim())
}

/**
 * Shared artwork/native appearance editor. Color variables are operator-facing
 * controls; non-color variables and authored color expressions remain in the
 * advanced raw variable editor. `cardAspect` is the shape of the card the
 * editor previews (width over height) and `headerAspect` the shape of its
 * header picture, which the banner picture's framing box takes for the
 * whole-card and the header fill.
 */
export default function PrepackAppearanceControls({
  orgSlug,
  template,
  pack,
  onChange,
  cardAspect,
  headerAspect,
  onPaintingChange,
  disabled = false,
}) {
  const { branding, fallbackTemplate } = usePrepackPreview()
  const colorMode = branding?.darkMode ? 'dark' : 'light'
  const activeTemplate = useMemo(() => (
    template && !template.isFallback ? template : fallbackTemplate || getFallbackTemplate([])
  ), [template, fallbackTemplate])
  const variant = useMemo(() => getTemplateVariant(activeTemplate, colorMode), [activeTemplate, colorMode])
  const currentVars = pack?.template_vars && typeof pack.template_vars === 'object' ? pack.template_vars : {}
  // A pack whose schedule chose another fill than the template's (issue 683) is painted and framed in that fill,
  // from that fill's default framing, as the card draws it.
  const packFill = currentVars[BANNER_FILL_VARIABLE]
  const bannerVariant = useMemo(() => getTemplateVariant(templateWithBannerFill(activeTemplate, packFill), colorMode), [activeTemplate, packFill, colorMode])
  const isNative = Boolean(activeTemplate?.isFallback)
  const inheritedVars = useMemo(() => {
    const resolved = resolveBrandingWithOverrides(branding, variant.branding)
    return brandingToCssVars(resolved)
  }, [branding, variant])
  const nativeColorVars = isNative
    ? NATIVE_COLOR_VARIABLES.map((variable) => {
      const inherited = inheritedVars[variable.key]
      const fallback = variable.fallbackKey ? inheritedVars[variable.fallbackKey] : undefined
      const value = currentVars[variable.key] ?? inherited ?? fallback ?? ''
      return { ...variable, value }
    })
    : []
  const colorVars = isNative
    ? nativeColorVars.filter((variable) => (
      (!Object.prototype.hasOwnProperty.call(currentVars, variable.key) || isColorInputValue(currentVars[variable.key]))
      && isColorInputValue(variable.value)
    ))
    : variant.cssVars.filter((variable) => (
      variable.type === 'color'
      && (!Object.prototype.hasOwnProperty.call(currentVars, variable.key) || isColorInputValue(currentVars[variable.key]))
      && isColorInputValue(currentVars[variable.key] ?? variable.value)
    ))
  // The Default card restyles exactly the colors its pickers show, at the
  // values they show: only the browser resolves the branding they inherit.
  const nativeColors = isNative
    ? Object.fromEntries(colorVars.map((variable) => [variable.key, colorInputValue(variable.value)]))
    : undefined
  // A builder template with an image banner: the pack settles its picture here.
  const bannerImage = bannerImageOf(activeTemplate)
  const bannerFill = packBannerFill(activeTemplate, currentVars)
  const advancedVars = isNative
    ? nativeColorVars.filter((variable) => {
      const hasOverride = Object.prototype.hasOwnProperty.call(currentVars, variable.key)
      return (hasOverride && !isColorInputValue(currentVars[variable.key])) || (!hasOverride && !isColorInputValue(variable.value))
    })
    : variant.cssVars.filter((variable) => (
      // The builder's banner picture and framing have their own controls; they are never typed in.
      (variable.type !== 'color' || !isColorInputValue(currentVars[variable.key] ?? variable.value)) && !(activeTemplate?.builder && variable.key.startsWith('--banner-'))
    ))

  const handleVarChange = (key, val) => {
    if (disabled) return
    onChange?.({ ...currentVars, [key]: val })
  }

  const styleActionKey = `${orgSlug || ''}:${activeTemplate?.id || 'native'}:${colorMode}:${pack?.id || 'preview'}`

  return (
    <div className="w-full flex flex-col gap-3">
      {activeTemplate ? (
        <PrepackStyleAction
          key={styleActionKey}
          orgSlug={orgSlug}
          template={activeTemplate}
          nativeColors={nativeColors}
          pack={{ ...pack, template_vars: currentVars }}
          disabled={disabled}
          onChange={onChange}
        />
      ) : null}

      {bannerImage ? <BannerPictureControls key={activeTemplate.id} orgSlug={orgSlug} templateId={activeTemplate.id} image={bannerImage} fill={bannerFill} title={bannerTitleOf(activeTemplate.builder?.title, bannerFill)} layout={activeTemplate.builder?.layout} cardAspect={cardAspect} headerAspect={headerAspect} pack={pack} vars={currentVars} variant={bannerVariant} disabled={disabled} onChange={onChange} onPaintingChange={onPaintingChange} /> : null}

      {colorVars.length > 0 ? (
        <fieldset className="grid gap-2.5 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-3">
          <legend className="px-1 text-xs font-semibold text-[var(--prepacks-muted)]">Card colors</legend>
          {colorVars.map((variable) => {
            const rawValue = currentVars[variable.key]
            const value = isColorInputValue(rawValue) ? rawValue : variable.value
            return (
              <label key={variable.key} className="flex items-center justify-between gap-3 text-[var(--wf-color-text)]">
                <span className="truncate text-xs text-[var(--prepacks-muted)]">{variable.label || variable.key}</span>
                <input
                  type="color"
                  aria-label={variable.label || variable.key}
                  disabled={disabled}
                  value={colorInputValue(value)}
                  onChange={(event) => handleVarChange(variable.key, event.target.value)}
                  className="h-8 w-10 cursor-pointer rounded border border-[var(--prepacks-border)] bg-transparent p-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </label>
            )
          })}
        </fieldset>
      ) : null}

      {advancedVars.length > 0 ? (
        <details className="w-full text-xs">
          <summary className="cursor-pointer text-[var(--prepacks-accent-text)] hover:underline">Advanced appearance values</summary>
          <div className="mt-3 grid gap-2.5 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-3">
            <p className="text-[11px] text-[var(--prepacks-muted)]">Use these fields for custom CSS values such as gradients and sizes.</p>
            {advancedVars.map((variable) => (
              <label key={variable.key} className="flex items-center justify-between gap-3 text-[var(--wf-color-text)]">
                <span className="truncate text-xs text-[var(--prepacks-muted)]">{variable.label || variable.key}</span>
                <input
                  aria-label={`${variable.label || variable.key} override`}
                  disabled={disabled}
                  className="w-40 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2 py-1 text-xs text-[var(--wf-color-text)] outline-none focus:border-[var(--prepacks-accent-text)] disabled:opacity-50"
                  value={currentVars[variable.key] ?? variable.value}
                  onChange={(event) => handleVarChange(variable.key, event.target.value)}
                />
              </label>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  )
}

const FRAMING_VARS = [BANNER_POSITION_VARIABLE, BANNER_SIZE_VARIABLE]

/** The variables without the given keys. */
function without(vars, keys) {
  return Object.fromEntries(Object.entries(vars).filter(([key]) => !keys.includes(key)))
}


/**
 * The pack's banner picture, settled while the pack is built (#606): generated now from a scene (only when the
 * template allows AI pictures), picked from the library, the template's own, or none (the card's colors). Every
 * choice marks the pack `banner_mode: static`, so the banner worker never generates over it. A pack that is removed
 * keeps `banner_hidden`, never a stored `none`. `onPaintingChange` tracks pending generation and uploads,
 * so the page can wait for it before saving. `fill` is where the template puts every picture, whatever its source
 * (header or whole card), which sets a generated picture's shape, the framing and its zoom's unit. `title` is
 * where the template puts the pack title on it (#946), which sets what the framing box draws over the picture.
 */
function BannerPictureControls({ orgSlug, templateId, image, fill, title, cardAspect, headerAspect, layout, pack, vars, variant, disabled, onChange, onPaintingChange }) {
  const { painting, paint: paintBanner, cancel: cancelPaint } = usePaintBanner(orgSlug)
  const [uploading, setUploading] = useState(false)
  const picturePending = painting || uploading
  const mediaAccess = usePictureMediaAccess()
  // The last generation's failure, shown in the panel as well as the toast; the next attempt or any other picture action clears it.
  const [paintError, setPaintError] = useState('')
  // A URL typed into the picker that is not a usable picture yet: it stays in the field and is never applied.
  const [typedUrl, setTypedUrl] = useState(null)
  // A picture takes a while and the other controls stay live: every result (a generated picture, a finished
  // upload) merges into the variables as they are then, never as they were when it started.
  const varsRef = useRef(vars)
  varsRef.current = vars
  const actionsRef = useRef(null)
  const pickerRef = useRef(null)
  const paintingChange = useRef(onPaintingChange)
  paintingChange.current = onPaintingChange
  // Started, then ended: by the answer, a failure, a template or game change, or the panel going away.
  useEffect(() => {
    if (!picturePending) return undefined
    paintingChange.current?.(true)
    return () => paintingChange.current?.(false)
  }, [picturePending])
  // The panel goes away (the template has no image banner, or the page closes): nothing lands afterwards.
  // A picture belongs to the template and the games it started on: switching templates (which also
  // clears the variables) or changing the legs abandons it, so it never lands on another
  // template's pack or shows the wrong game.
  const legs = Array.isArray(pack?.legs) ? pack.legs : []
  const identity = JSON.stringify([pack?.template_id ?? null, pack?.sport || legs[0]?.sport || '', pack?.league || legs[0]?.league || '', legs.map((leg) => leg.event_label || leg.eventLabel || '')])
  const identityRef = useRef(identity)
  identityRef.current = identity
  useEffect(() => { cancelPaint(); setTypedUrl(null); setPaintError('') }, [identity, cancelPaint])
  const own = bannerImageUrl(vars[BANNER_IMAGE_VARIABLE])
  const removed = vars[BANNER_HIDDEN_VAR] === true
  const shown = own || (!removed && image.url) || null
  // Framing: the pack's own, else the template's defaults.
  const templateVar = (key) => variant.cssVars.find((variable) => variable.key === key)?.value
  const position = vars[BANNER_POSITION_VARIABLE] ?? templateVar(BANNER_POSITION_VARIABLE)
  const scale = bannerScaleFromSize(vars[BANNER_SIZE_VARIABLE] ?? templateVar(BANNER_SIZE_VARIABLE)) ?? 1
  // The framing box wears what the card puts over the picture (bannerFramingWash), in the pack's own colors where it
  // set them; without a picture the card shows its header color, or the banner fade behind the whole card.
  const colorOf = (key) => vars[key] || templateVar(key) || '#000000'
  const shade = bannerFramingWash({ fill, title, main: colorOf('--header'), headerEnd: colorOf('--header-end') })
  const main = colorOf(fill === BANNER_FILL_CARD ? '--header-end' : '--header')
  const setFraming = (framing) => onChange?.({ ...vars, [BANNER_POSITION_VARIABLE]: framing.position, [BANNER_SIZE_VARIABLE]: bannerImageSize(framing.scale, fill) })
  const templatePrompt = image.prompt || ''
  const prompt = typeof vars[BANNER_PROMPT_VAR] === 'string' ? vars[BANNER_PROMPT_VAR] : templatePrompt
  const setPrompt = (value) => {
    const { [BANNER_PROMPT_VAR]: _own, ...rest } = vars
    // The template's own scene needs no copy on the pack.
    onChange?.(value.trim() === templatePrompt.trim() ? rest : { ...rest, [BANNER_PROMPT_VAR]: value })
  }
  // A new picture (or none) starts from the template's framing, and the pack is settled: nobody generates it later.
  const settle = (current, picture) => {
    const rest = without(current, [BANNER_IMAGE_VARIABLE, BANNER_HIDDEN_VAR, ...FRAMING_VARS])
    if (picture === null) return { ...rest, [BANNER_MODE_VAR]: BANNER_MODE_STATIC }
    if (picture === '') return { ...rest, [BANNER_HIDDEN_VAR]: true, [BANNER_MODE_VAR]: BANNER_MODE_STATIC }
    return { ...rest, [BANNER_IMAGE_VARIABLE]: bannerImageValue(picture), [BANNER_MODE_VAR]: BANNER_MODE_STATIC }
  }
  // The button that was used may be gone: the focus moves to the first action still there, else to the picker.
  const keepFocus = () => requestAnimationFrame(() => {
    const active = document.activeElement
    if (actionsRef.current?.contains(active) || pickerRef.current?.contains(active)) return
    const next = actionsRef.current?.querySelector('button:not([disabled])') || pickerRef.current?.querySelector('button:not([disabled])')
    next?.focus()
  })
  const generate = () => {
    const scene = prompt.trim()
    if (disabled || !scene || picturePending) return
    setPaintError('')
    const startedOn = identity
    void paintBanner({
      prompt: scene,
      fill,
      layout,
      templateId,
      prepackId: typeof pack?.id === 'string' && pack.id !== 'preview' ? pack.id : undefined,
      sport: pack?.sport || legs[0]?.sport || '',
      league: pack?.league || legs[0]?.league || '',
      legs,
    }, {
      onSuccess: (result) => {
        if (identityRef.current === startedOn) onChange?.(settle(varsRef.current, result.url))
      },
      onError: (message) => {
        if (identityRef.current === startedOn) {
          setPaintError(message)
          toast.error(message)
        }
      },
    })
  }
  // A pick can land long after it started (an upload): it settles the variables as they are by then.
  const pick = (_, url) => {
    if (disabled) return
    setPaintError('')
    const picked = String(url || '').trim()
    if (isBannerImageUrl(picked)) {
      setTypedUrl(null)
      const current = varsRef.current
      if (picked !== bannerImageUrl(current[BANNER_IMAGE_VARIABLE])) onChange?.(settle(current, picked))
    } else {
      setTypedUrl(picked)
    }
  }
  const showTemplatePicture = () => {
    if (disabled || picturePending) return
    setPaintError('')
    onChange?.(settle(vars, null))
    keepFocus()
  }
  const remove = () => {
    if (disabled || picturePending) return
    setPaintError('')
    onChange?.(settle(vars, ''))
    keepFocus()
  }
  let caption
  if (painting) caption = 'Generating the picture for this pack…'
  else if (own) caption = 'This pack’s picture.'
  else if (shown) caption = 'The template’s picture.'
  else if (removed) caption = 'No picture. The card shows its colors.'
  else if (vars[BANNER_MODE_VAR] === BANNER_MODE_DYNAMIC) caption = 'A picture is generated for this pack about a minute after it is created. Until then the card shows its colors.'
  else if (image.ai_allowed) caption = 'Generate one now, or pick one.'
  else caption = 'Pick a picture, or the card shows its colors.'
  const typedInvalid = Boolean(typedUrl)
  return (
    <fieldset className="grid min-w-0 gap-2.5 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-3 text-xs">
      <legend className="px-1 text-xs font-semibold text-[var(--prepacks-muted)]">Banner picture</legend>
      {/* The painting state and its outcome are announced. */}
      <p role="status" className="text-[11px] text-[var(--prepacks-muted)]">{caption}</p>
      <p aria-live="polite" className={paintError ? 'text-[11px] text-[var(--wf-color-danger)]' : 'sr-only'}>{paintError}</p>
      {/* One preview: the picture as the card crops it, or the card's colors where it has none. */}
      {shown
        ? <BannerFraming picture={shown} main={shade.main} wash={shade.wash} fill={fill} aspect={cardAspect} headerAspect={headerAspect} position={position} scale={scale} disabled={disabled || picturePending} onChange={setFraming} />
        : <span aria-hidden="true" className="block aspect-[5/1] w-full rounded-lg border border-[var(--prepacks-border)]" style={{ background: main }} />}
      {image.ai_allowed ? (
        <input aria-label="Scene" name="banner-scene" autoComplete="off" value={prompt} disabled={disabled || painting} placeholder={templatePrompt || 'Stadium at night under floodlights…'} title={prompt || undefined}
          onChange={(event) => setPrompt(Array.from(event.target.value).slice(0, MAX_BANNER_PROMPT_RUNES).join(''))}
          className="w-full rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2 py-1.5 text-xs text-[var(--wf-color-text)] focus:border-[var(--prepacks-accent-text)] disabled:opacity-50" />
      ) : null}
      <div ref={actionsRef} className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {image.ai_allowed ? (
          // Not disabled while generating: the focus stays on it, and it says so until the picture lands.
          <button type="button" onClick={generate} disabled={disabled || uploading || (!painting && !prompt.trim())} aria-disabled={painting || undefined}
            className="flex items-center gap-1.5 rounded border border-[var(--prepacks-border)] px-2.5 py-1.5 font-medium text-[var(--prepacks-text)] hover:bg-[var(--prepacks-raised)] disabled:opacity-50 aria-disabled:opacity-70">
            {painting ? <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />}
            {painting ? 'Generating picture…' : 'Generate picture'}
          </button>
        ) : null}
        {(own || removed) && image.url ? <button type="button" disabled={disabled || picturePending} onClick={showTemplatePicture} className="-my-1 px-1 py-1 text-[var(--prepacks-accent-text)] hover:underline disabled:opacity-50">Use the template’s picture</button> : null}
        {shown ? <button type="button" disabled={disabled || picturePending} onClick={remove} className="-my-1 px-1 py-1 text-[var(--prepacks-accent-text)] hover:underline disabled:opacity-50">Remove</button> : null}
      </div>
      {/* The library picker writes no Remove of its own: "Remove" above is the one that shows the colors. The
          picture itself shows above, so the picker shows none. */}
      <div ref={pickerRef}>
        <NativeAssetControl slug={orgSlug} field={{ path: 'url', label: 'Pack picture', required: true }} value={typedUrl ?? own ?? ''} disabled={disabled || painting} preview={false} {...mediaAccess} onChange={pick} onBusyChange={setUploading} />
      </div>
      {typedInvalid ? <p role="alert" className="text-[var(--wf-color-danger)]">Pictures load from https URLs without spaces or quotes.</p> : null}
    </fieldset>
  )
}
